import { GestureEventListeners } from '@polymer/polymer/lib/mixins/gesture-event-listeners.js';
import { PolymerElement, html } from '@polymer/polymer';

import '@polymer/polymer/lib/elements/dom-if.js';
import '@polymer/iron-flex-layout/iron-flex-layout.js';
import '@polymer/iron-a11y-keys/iron-a11y-keys.js';
import '@wdpr/color';
import '@wdpr/mdx-icons';

import './style/dcl-modal-style.js';
import './shadowlisten.js';

class DclModal extends GestureEventListeners(PolymerElement) {

  static get is() {
    return 'dcl-modal';
  }

  static get template() {
    return html`
      <style include="wdpr-color"></style>
      <style include="wdpr-mdx-icons"></style>
      <style include="dcl-modal-style"></style>

      <!-- #wrapper-outer is needed because MS Edge is broken with position: fixed; & flexbox -->
      <div id="wrapperOuter" on-tap="_closeOnBackground" class\$="[[_getOuterWrapperClass(fullscreen, docker)]]">
        <!-- #wrapper is what guest visually sees as the modal (has background and border) -->
        <div id="wrapper" class\$="[[_getWrapperClass(noPadding)]]" role="dialog">

          <!-- modal content -->
          <div id="content">

            <div role="heading" aria-hidden="true" class\$="header [[_setTitleSpacing(hideCloseIcon, fullscreen)]]" id="title" hidden\$="[[!_hasTitle]]">
              <slot id="titleSlot" name="title"></slot>
            </div>

            <hr class="title-and-body-separator" hidden\$="[[hideTitleBodySeparator]]">
            <!-- note: adding tabindex-1 here because firefox makes body tabbable if there is a scrollbar -->
            <div id="body" class\$="[[_getBodyClass(_hasScrollbar, noPadding)]]" tabindex="-1">
              <template is="dom-if" if="[[ariaStart]]">
                <span id="ariaStart" class="screen-reader-text">[[ariaStart]]</span>
              </template>
              <span class="screen-reader-text">[[_slottedTitle]]</span>
              <slot id="bodySlot" name="body"></slot>
              <template is="dom-if" if="[[ariaEnd]]">
                <span id="ariaEnd" class="screen-reader-text">[[ariaEnd]]</span>
              </template>
            </div>
          </div>

          <!-- Note: close icons & buttons are extracted out of #content for screen readers -->

          <!-- close icon for mobile (fullscreen) view -->
          <template is="dom-if" if="[[!hideCloseIcon]]">
            <div
            id="closeArrow"
            class="close"
            on-tap="_close"
            tabindex="0"
            aria-label\$="[[ariaClose]]"
            data-callback-key\$="[[callbackCloseKey]]">
              <i class="di di-previous-caret-2" data-callback-key\$="[[callbackCloseKey]]"></i>
              <iron-a11y-keys keys="enter space" on-keys-pressed="_close" data-callback-key\$="[[callbackCloseKey]]"></iron-a11y-keys>
            </div>
          </template>

          <!-- close x for tablet/desktop (dialog) view -->
          <template is="dom-if" if="[[!hideCloseIcon]]">
            <div
              id="closeX"
              class="close"
              on-tap="_close"
              tabindex="0"
              role="button"
              aria-label\$="[[ariaClose]]"
              data-callback-key\$="[[callbackCloseKey]]">
              <iron-a11y-keys
                keys="enter space"
                on-keys-pressed="_close"
                data-callback-key\$="[[callbackCloseKey]]"></iron-a11y-keys>
              <svg
                focusable="false"
                stroke-width="4"
                stroke="currentColor"
                stroke-linecap="round"
                data-callback-key\$="[[callbackCloseKey]]">
                <path d="M3,3,21,21" data-callback-key\$="[[callbackCloseKey]]"/>
                <path d="M3,21,21,3" data-callback-key\$="[[callbackCloseKey]]"/>
              </svg>
            </div>
          </template>

          <!-- note: cannot use dom-if because it destroys the slot which is needed to compute _hasButtons -->
          <hr id="buttonsSeparator" hidden\$="[[_shouldHideButtonsSeparator()]]">
          <div id="buttonsWrapper" hidden\$="[[!_hasButtons]]">
            <slot id="buttons" name="buttons"></slot>
            <!-- note: spacer is used to force buttons to shrink down to smaller size -->
            <div id="buttons-spacer"></div>
          </div>
        </div>
      </div>
    `;
  }

  static get properties() {
    return {
      /**
       * @desc show modal
       * @type Boolean
       */
      open: {
        type: Boolean,
        value: false,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * @desc triggers close animation
       * @type Boolean
       */
      close: {
        type: Boolean,
        value: false,
        computed: '_closeEvent(close)',
        reflectToAttribute: true
      },
      /**
       * @desc dcl modal id type
       * @type String
       */
      dockerModal: {
        type: String,
        value: 'docker-modal'
      },
      /**
       * @desc hide close icon
       * @type Boolean
       */
      hideCloseIcon: {
        type: Boolean,
        value: false
      },
      /**
       * @desc aria-hidden is applied when modal is closed
       * @type String
       */
      ariaHidden: {
        type: String,
        computed: '_ariaHidden(open, _hasFocus)',
        reflectToAttribute: true
      },
      /**
       * @desc allow closing the modal by clicking on the background
       * @type Boolean
       */
      disableCloseOnBackground: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Toggle assignation functionality tabindex property in slot buttons
       * @type Boolean
       */
      disableTabIndexOnSlotButtons: {
        type: Boolean,
        value: false
      },
      /**
       * @desc active scroll event on modal
       * @type Boolean
       */
      activeScrollEvent: {
        type: Boolean,
        value: false
      },
      /**
       * @description scroll pixel size
       * @type Number
       */
      lastScroll: Number,
      /**
       * @desc disable closing the modal by pressing escape
       * @type Boolean
       */
      disableCloseOnEscape: {
        type: Boolean,
        value: false
      },
      /**
       * @desc force fullscreen regardless of screensize
       * @type Boolean
       */
      fullscreen: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Enable docker modal
       * @type Boolean
       */
      docker: {
        type: Boolean,
        value: false
      },
      /**
       * @desc length of title text before resize
       * @type Number
       */
      titleResizeLength: {
        type: Number,
        value: 25
      },
      /**
       * @desc screen reader text read at start of modal
       * @type String
       */
      ariaStart: String,
      /**
       * @desc screen reader text read at end of modal
       * @type String
       */
      ariaEnd: String,
      /**
       * @desc screen reader text for close button
       */
      ariaClose: String,
      /**
       * @desc css variables to apply to modal
       * @type {Object}
       */
      styles: Object,
      /**
       * @desc set to true to enable A11Y feature to block focus from leaving modal
       * @type Boolean
       */
      blockFocus: {
        type: Boolean,
        value: false
      },
      /**
       * @desc set to true to remove wrapper padding
       * @type Boolean
       */
      noPadding: {
        type: Boolean,
        value: false
      },
      /**
       * @desc set to true to prevent set the focus to the modal body when modal opens
       * @type Boolean
       */
      preventFocusBody: {
        type: Boolean,
        value: false
      },
      /**
       * @desc private property used in template to show button content
       * @type Boolean
       */
      _hasButtons: {
        type: Boolean,
        value: false
      },
      /**
       * @desc private property used in styling
       * @type Boolean
       */
      _hasScrollbar: {
        type: Boolean,
        value: false
      },
      /**
       * @desc private property used in template to show title content
       * @type Boolean
       */
      _hasTitle: {
        type: Boolean,
        value: false
      },
      /**
       * @desc private callback - stored on a property so listener can be removed with removeEventListener
       * @type Function
       */
      _focusBlockerListener: Function,
      /**
       * @desc private property used to store element that opened the modal - focus is returned after closing modal
       * @type HTMLElement
       */
      _openedBy: HTMLElement,
      /**
       * @desc let internal aria listener know that the modal is focused so that we know when to adjust aria-hidden property
       * @type {Boolean}
       */
      _hasFocus: {
        type: Boolean,
        value: false
      },
      /**
       * @desc slotted title text -- grab this for a11y placement
       * @type {String}
       */
      _slottedTitle: String,
      /**
       * @desc a11y dom placement complete
       * @type {Boolean}
       */
      _a11yDomPlacementComplete: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Scroll data
       * @type {String}
       */
      _scrollModal: {
        type: String,
        value: ''
      },
      /**
       * Attribute that will reflect which key is been triggered
       * @type {string}
       */
      callbackKeyTriggered: {
        type: String,
        value: '',
        notify: true,
        reflectToAttribute: true
      },
      /**
       * Callback event keys definition
       * @type {{String}}
       */
      callbackCloseKey: {
        type: String,
        value: 'closeButtonClicked'
      },
      /**
       * @desc property used in template to hide title and body separator
       * @type Boolean
       */
      hideTitleBodySeparator: {
        type: Boolean,
        value: false
      },
      /**
       * @desc property used in template to hide buttons separator
       * @type Boolean
       */
      hideButtonsSeparator: {
        type: Boolean,
        value: false
      },
      /**
       * @desc property used to disable the functionality of
       *  hiding elements at the same level of the modal
       * @type Boolean
       */
      disableElementsToHide: {
        type: Boolean,
        value: false
      },
      /**
       * @desc Force autofocus on closeX button
       * @type Boolean
       */
      autoFocusOnCloseButton: {
        type: Boolean,
        value: false
      }
    };
  }

  /**
   * @desc property observers
   * @return {Array} observers
   */
  static get observers() {
    return [
      '_focusBehavior(open, _a11yDomPlacementComplete)',
      '_scrollBehavior(open, fullscreen, docker)',
      '_a11yDomPlacement(open)'
    ];
  }

  /**
   * Instance of the element is created/upgraded. Use: initializing state,
   * set up event listeners, create shadow dom.
   * @constructor
   */
  constructor() {
    super();
    this._printFunction = this._beforePrint.bind(this);
    this._removePrintFunction = this._afterPrint.bind(this);
  }

  ready() {
    super.ready();
    this._checkBrowser();

    this.lastScroll = 0;

    // check to see if consumer slotted in buttons -- if there are buttons we need to adjust visual display
    // https://github.com/webcomponents/gold-standard/wiki/Content-Changes#monitoring-content-changes-with-slotchange-events
    let hasButtons = () => {
      const nodesSlotButtons = this.$.buttons.assignedNodes();

      this._hasButtons = nodesSlotButtons.length > 0;

      if (!this.disableTabIndexOnSlotButtons) {
        nodesSlotButtons.map(node => node.setAttribute('tabindex', 0));
      }
    }
    hasButtons();
    this.$.buttons.addEventListener('slotchange', event => hasButtons(event));

    if (this.activeScrollEvent) {
      this.$.body.addEventListener('scroll',
        () => this._handleBodySCroll(),
        { passive: true }
      );
    }

    // update slotted title so we can use for screen reader text
    let updateSlottedTitle = () => {
      let nodes = this.$.titleSlot.assignedNodes();
      this._hasTitle = nodes.length > 0;
      this._slottedTitle = nodes.map(node => node.textContent).join();
      if (this._slottedTitle.length > this.titleResizeLength) {
        this.$.wrapper.classList.add('resize');
      } else {
        this.$.wrapper.classList.remove('resize');
      }
      nodes.map(node => node.setAttribute('tabindex', 0));
    }
    updateSlottedTitle();
    this.$.titleSlot.addEventListener('slotchange', event => {
      updateSlottedTitle(event);
      this._shouldHideTitleBodySeparator();
    });

    let updateSlottedBody = () => {
      let nodes = this.$.bodySlot.assignedNodes();
      nodes.map(node => node.setAttribute('tabindex', -1));
    }
    updateSlottedBody();
    this.$.bodySlot.addEventListener('slotchange', event => updateSlottedBody(event));

    this.addEventListener('tap', (event) => {
      event.stopPropagation();
    });

    document.addEventListener('keydown', this._closeOnEscape.bind(this));

    let transitionEvent = this._detectTransition();
    this.$.wrapper.addEventListener(transitionEvent, () => {
      if (this.$.wrapper.classList.contains('slide-right')) {
        this.open = false;
      }
    });
  }

  /**
   * dispatchEvent custom event linked to scroll event.
   */
  _handleBodySCroll() {
    const direction = this.$.body.scrollTop > 0 && this.lastScroll <= this.$.body.scrollTop ? 1 : 0;

    if (this.open) {
      this.dispatchEvent(new CustomEvent('scrollEventEmitter', {
        detail: {
          scrollTop: this.$.body.scrollTop,
          down: direction === 1,
          up: direction === 0
        }
      }));

      this.lastScroll = this.$.body.scrollTop;
    }
  }

  /**
   * Called every time the element is removed from the DOM. Useful for
   * running clean up code (removing event _instancePrintEventslisteners, etc.).
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('beforeprint',this._printFunction);
    window.removeEventListener('afterprint',this._removePrintFunction);
    this.$.body.removeEventListener('scroll', () => this._handleBodySCroll())
  }

  /**
   * Called every time the element is removed from the DOM. Useful for
   * running clean up code (removing event _instancePrintEventslisteners, etc.).
   */
  connectedCallback() {
    super.connectedCallback();
    let printQuery;
    let screenQuery;

    if (this.isSafari) {
      printQuery = window.matchMedia('print');

      printQuery.addListener(function(print) {
        screenQuery = window.matchMedia('screen');

        this._beforePrint();

        screenQuery.addListener(function(screen) {
          if (screen.matches) {
            this._afterPrint();
          }
        });
      });
    } else if (this.isChrome) {
      window.addEventListener("beforeprint", this._printFunction);
      window.addEventListener("afterprint", this._removePrintFunction);
    }
  }

  /**
   * @desc A11y requires that the modal be placed outside of all other content
   * This allows us to apply aria-hidden content outside of the modal for screen readers
   * However, when we do this, there are a couple core things that break:
   *
   * 1) Nested slots stop working
   * 2) Styles go missing because the modal is no longer contained within the original shadow tree
   *
   * This method moves the modal while addressing the above issues ^^
   *
   * @param {Boolean} open
   */
  _a11yDomPlacement(open) {
    if (open) {
      if (this.id == "docker-modal") {
        this._dockerModalAnimation(true);

        if (this.autoFocusOnCloseButton) {
          this._autoFocusCloseButton();
        }
      }
      // create dom wrapper to house the modal
      this.domWrapper = document.createElement('div');
      this.domWrapper.classList.add('dcl-modal-dom-wrapper');

      // account for nested slots -- when removing the modal from original dom placement, nested slots break
      // solution is to append to slot so that becomes the default content
      // example: <div slot="body" class="type18"><slot name="modal-body"></slot></div>
      let bodyContent = this.$.bodySlot.assignedNodes();
      bodyContent = Array.prototype.slice.call(bodyContent)
        .filter(node => node.nodeType === Node.ELEMENT_NODE)
        .forEach(node => {
          Array.prototype.slice.call(node.childNodes)
            .filter(node => node.tagName === 'SLOT')
            .forEach(node => {
              node.assignedNodes().forEach(assignedNode => node.appendChild(assignedNode));
            });
        });

      // preserve styles from parent element
      // note: code assumes 1:1 relationship between element constructor and dom-module
      let domModule = this.parentNode && this.parentNode.host && this.parentNode.host.constructor.is;
      if (domModule) {
        let customStyle = document.createElement('custom-style'),
          style = document.createElement('style');

        style.setAttribute('include', domModule);
        customStyle.appendChild(style);
        this.domWrapper.appendChild(customStyle);
      }

      if (this.styles) {
        this.updateStyles(this.styles);
      }

      // remove the modal from current parent
      this._parentNode = this.parentNode;
      this.parentNode.removeChild(this);

      // these tabindex elements allow the focus to be trapped before leaving the dom
      // we cannot detect the focus if the next tabindex is browser navigation controls
      let divWithTabIndex = document.createElement('div');
      divWithTabIndex.setAttribute('tabindex', 0);
      divWithTabIndex.setAttribute('aria-hidden', true);
      divWithTabIndex.style.outline = 'none';

      this.domWrapper.appendChild(divWithTabIndex.cloneNode());
      this.domWrapper.appendChild(this);
      this.domWrapper.appendChild(divWithTabIndex.cloneNode());

      document.body.appendChild(this.domWrapper);
      this._a11yDomPlacementComplete = true;
    } else if (this._parentNode) {
      this.domWrapper.parentNode.removeChild(this.domWrapper);
      this._parentNode.appendChild(this);
      this._a11yDomPlacementComplete = false;
    }
  }

  /**
   * Triggers docker modal animation by closing modal with external custom close buttons 
   */
  _closeEvent(close) {
    if (close && this.id == this.dockerModal) {
      this._dockerModalAnimation(false, 'closedModalByCloseIcon');
    }
  }

  /**
   * @desc check if button spacer should be hidden
   * @param {Number} button count
   * @return {Boolean} hide button spacer
   */
  _hideButtonSpacer(buttonCount) {
    return buttonCount > 1;
  }

  /**
   * @desc hide modal content from screen readers when modal is not visible
   * @param {Boolean} open
   * @param {Boolean} hasFocus
   * @return {String} aria hidden
   */
  _ariaHidden(open, hasFocus) {
    // when modal is open, we need to make sure other content is not accessible by screen readers
    let elements = this._getElementsToHide();

    elements.forEach(element => {
      if (open && hasFocus) {
        // if element already has aria-hidden, store the value so we can restore later
        let ariaHiddenValue = element.hasAttribute('aria-hidden');
        if (ariaHiddenValue) {
          element.setAttribute('dcl-modal-aria-hidden', true);
        }
        element.setAttribute('aria-hidden', true);
      } else if (!open) {
        // revert element to previous aria-hidden value
        let ariaHiddenValue = element.hasAttribute('dcl-modal-aria-hidden');
        element.removeAttribute('dcl-modal-aria-hidden');
        if (!ariaHiddenValue) {
          element.removeAttribute('aria-hidden');
        }
      }
    });

    return open ? 'false' : 'true';
  }

  /**
   * @desc get elements on the page that we need to hide when the modal is open
   * @return {HTMLElement[]} elements
   */
  _getElementsToHide() {
    // Note: `body > div` is current requirement for PEPCOM, but this can be extended if we need to hide additional content.
    let elements = document.querySelectorAll('body > div, body > img');
    return Array.prototype.slice.call(elements)
      .filter(elem => !elem.classList.contains('dcl-modal-dom-wrapper'));
  }

  /**
   * @desc add spacing between modal title and close button (X Icon)
   * @param {Boolean} hideCloseIcon
   * @param {Boolean} fullscreen
   * @return {String} class
   */
  _setTitleSpacing(hideCloseIcon, fullscreen) {
    return !hideCloseIcon && !fullscreen ? 'has-spacing' : '';
  }

  /**
   * @desc setup scroll behavior
   * @param {Boolean} open
   * @param {Boolean} fullscreen
   */
  _scrollBehavior(open, fullscreen, docker) {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // scroll modal back to top when open/closed
    // Note: needed even when closing the modal to prevent the window from scrolling unintentially after closing the modal.
    this.$.body.scrollTop = 0;
    // check if the modal body has a scrollbar
    this._hasScrollbar = this.$.body.scrollHeight > this.$.body.clientHeight;

    // prevent page content shift when hiding the scrollbar (IE11, Edge)
    let width = this._getScrollbarWidth();
    document.body.style.marginRight = open ? width + 'px' : 'auto';

    // disable scrolling on body when modal is open.
    document.body.style.overflowY = open ? 'hidden' : 'auto';

    // on mobile devices we want to take futher steps so that the guest cannot scroll page content
    if (open) {
      let doc = document.documentElement;
      this._originalScroll = (window.pageYOffset || doc.scrollTop) - (doc.clientTop || 0);
    }

    if (iOS) {
      document.body.style.position = open ? 'fixed' : 'static';
    }

    // hide other elements so it doesn't become visible behind the modal (scroll bounce)
    if ((fullscreen || docker) && !this.disableElementsToHide) {
      let elements = this._getElementsToHide();
      elements.forEach(element => {
        if (open) {
          element.setAttribute('dcl-modal-display', element.style.display);
          element.style.display = 'none';
        } else if (!open) {
          // revert element to previous display value
          let displayValue = element.getAttribute('dcl-modal-display');
          element.removeAttribute('dcl-modal-display');
          element.style.display = displayValue;
        }
      });
    }

    if (!open && this._originalScroll) {
      window.scrollTo(0, this._originalScroll);
    }
  }

  /**
   * @desc class for modal #body
   * @param {Boolean} if body has scrollbar
   * @return {String}
   */
  _getBodyClass(hasScrollbar, noPadding) {
    return hasScrollbar && !noPadding ? 'has-scrollbar' : '';
  }

  /**
   * @desc get outer wrapper class for styling
   * @return {String} class [fullscreen, dialog]
   */
  _getWrapperClass(noPadding) {
    return noPadding ? 'no-padding' : '';
  }

  /**
   * @desc get outer wrapper class for styling
   * @param {Boolean} fullscreen forced
   * @return {String} class [fullscreen, dialog]
   */
  _getOuterWrapperClass(fullscreen, docker) {
    if (docker) {
      return 'docker';
    } else {
      return fullscreen ? 'fullscreen' : 'dialog';
    }
  }

  /**
   * @desc setup listeners to block focus from leaving modal
   * https://www.w3.org/WAI/GL/wiki/Using_ARIA_role%3Ddialog_to_implement_a_modal_dialog_box
   * @param {Boolean} open
   * @param {Boolean} _a11yDomPlacementComplete
   */
  _focusBehavior(open, _a11yDomPlacementComplete) {
    if (open && _a11yDomPlacementComplete) {
      // store opening element so we can put the focus back at that element when closing the modal.
      let openedBy = document.activeElement;
      while (openedBy.shadowRoot && openedBy.shadowRoot.activeElement) {
        openedBy = openedBy.shadowRoot.activeElement;
      }
      this._openedBy = openedBy;

      if (!this.preventFocusBody) {
        this.setFocus(this.$.body);
      }

      // ariaHidden listener needs this to time when to set aria-hidden property
      this._hasFocus = true;

      if (this.blockFocus) {
        // create a focus blocker
        this._focusBlockerListener = this._focusBlocker.bind(this);
        document.addEventListener('-dcl-modal-shadow-focus', this._focusBlockerListener, false);
      }
    } else if (this._focusBlockerListener) {
      this._hasFocus = false;
      if (this.blockFocus) {
        // remove the focus blocker
        document.removeEventListener('-dcl-modal-shadow-focus', this._focusBlockerListener, false);
        this._focusBlockerListener = undefined;
      }
      // put focus back on opening element
      if (this._openedBy) {
        this._openedBy.focus();
      }
    }
  }

  /**
   * @desc Setting Focus with JavaScript (Supporting VoiceOver)
   * @see https://silvantroxler.ch/2016/setting-voiceover-focus-with-javascript/
   * @param {HTMLElement} element
   */
  setFocus(element) {
    let focusInterval = 10, // ms, time between function calls
      focusTotalRepetitions = 10; // number of repetitions

    element.blur();

    let focusRepetitions = 0;
    let interval = window.setInterval(() => {
      element.focus();
      focusRepetitions++;
      if (focusRepetitions >= focusTotalRepetitions) {
        window.clearInterval(interval);
      }
    }, focusInterval);
  }

  /**
   * @desc Block Focus from leaving the modal
   * @param {Event} event
   */
  _focusBlocker(event) {

    // event.detail contains the correct element under native shadow dom
    if (event.detail === this) return;
    if (this.shadowRoot.contains(event.detail)) return;
    if (this.contains(event.detail)) return;

    // handle situations where element is within another shadow root
    let elem = event.detail;
    while (elem && elem.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      elem = elem.parentNode;
    }
    if (elem && this.contains(elem.host)) return;

    // shadowRoot.activeElement and parentNode.activeElement is for IE11
    if (!this.shadowRoot || !this.parentNode) return;
    if (this.shadowRoot.activeElement) return;
    if (this.contains(this.parentNode.activeElement)) return;

    event.stopPropagation();

    if (this.open && this._originalScroll !== null && typeof this._originalScroll !== 'undefined') {
      window.scrollTo(0, this._originalScroll);
    }

    this.setFocus(this.$.body);
  }

  /**
   * @desc close the modal
   * @param {Event} event
   */
  _close(event) {
    this._afterPrint();
    this._callbackFunction(event);
    if (this.id === 'docker-modal') {
      this._dockerModalAnimation(false, 'closedModalByCloseIcon');
    } else {
      this.open = false;
      this.dispatchEvent(new CustomEvent('closedModalByCloseIcon'));
    }
    // prevent window from scrolling when closing the modal with spacebar
    // For keyboardEvent, see: https://github.com/PolymerElements/iron-a11y-keys-behavior
    if (event.detail.keyboardEvent) {
      event.detail.keyboardEvent.preventDefault();
    }
  }

  /**
   * Check browser
   */
  _checkBrowser() {
    let chrome = navigator.userAgent.search("Chrome");
    let firefox = navigator.userAgent.search("Firefox");
    let safari = navigator.userAgent.search("Safari");

    if (chrome > -1) {
      this.isChrome = true;
    } else if (firefox > -1) {
      this.isFirefox = true;
    } else if (safari > -1) {
      this.isSafari = true;
    }
  }

  /**
   * Remove class to the body after print
   */
  _afterPrint() {
    const pintStateClass = 'print-state-whats-included-modal';
    document.body.classList.remove(pintStateClass);
  }

  /**
   * Add class to the bocy before ptint
   */
  _beforePrint() {
    const dclModalSelector = document.querySelector('dcl-modal');
    const pintStateClass = 'print-state-whats-included-modal';
    if (dclModalSelector &&
        dclModalSelector.attributes &&
        dclModalSelector.attributes.open
    ) {
      document.body.classList.add(pintStateClass);
    }
  }

  /**
 * Detects the animation attribute according to the user's browser.
 * @return {String} Return the animation attribute name.
 */
  _detectTransition() {
    let tmpIndex,
        el = this.$.wrapper,
        transitions = {
          'animation': 'animationend',
          'OAnimation': 'oAnimationEnd',
          'MozAnimation': 'animationend',
          'WebkitAnimation': 'webkitAnimationEnd'
        }

    for (tmpIndex in transitions) {
      if (typeof el.style[tmpIndex] !== "undefined") {
        return transitions[tmpIndex];
      }
    }
  }

  /**
 * Set the animation on the docker modal
 * @param {Boolean} animation Boolean - Adjust the class name.
 * @param {String} event event name to dispatch
 * @return {void} Set the class name for the #wrapper element according at the behavior.
 */
  _dockerModalAnimation(animation, event) {
    if (animation) {
      this.$.wrapperOuter.classList.add('fadeIn');
      this.$.wrapperOuter.classList.remove('fadeOut');
    } else {
      this.$.wrapperOuter.classList.add('fadeOut');
      this.$.wrapperOuter.classList.remove('fadeIn');
    }
    this.$.wrapper.className = animation ? 'slide-left' : 'slide-right';

    if (event) {
      this.dispatchEvent(new CustomEvent(event));
    }
  }
  /**
   * @desc close on escape key
   * Extracting away from a11y-keys because this should work even when focus is outside of modal
   * Example: slotted body content is light dom
   * @param {Event} event
   */
  _closeOnEscape(event) {
    this._callbackFunction(event);
    if (!this.open) return;
    if (this.disableCloseOnEscape) return;
    event = event || window.event;
    let isEscape = false;
    if ("key" in event) {
      isEscape = (event.key === "Escape" || event.key === "Esc");
    } else {
      isEscape = (event.keyCode === 27);
    }
    if (!isEscape) return;
    this._afterPrint();
    if (this.id === "docker-modal") {
      this._dockerModalAnimation(false, 'closedOnEscape');
    } else {
      this.open = false;
      this.dispatchEvent(new CustomEvent('closedOnEscape'));
    }

  }

  /**
   * @desc close the modal when clicking on background
   * @param {Event} event
   */
  _closeOnBackground(event) {
    this._callbackFunction(event);
    if (this.disableCloseOnBackground) return;
    if (!event.target.contains(this.$.wrapperOuter)) return;
    this._afterPrint();
    if (this.id === "docker-modal") {
      this._dockerModalAnimation(false, 'closedOnBackgroundClick');
    } else {
      this.open = false;
      this.dispatchEvent(new CustomEvent('closedOnBackgroundClick'));
    }
  }

  /**
   * Get scrollbar width
   * https://stackoverflow.com/a/986977
   * @return {Number} width
   */
  _getScrollbarWidth() {
    let inner = document.createElement('p'),
        outer = document.createElement('div'),
        w1 = inner.offsetWidth,
        w2 = inner.offsetWidth;

    inner.style.width = "100%";
    inner.style.height = "200px";
    outer.style.position = "absolute";
    outer.style.top = "0px";
    outer.style.left = "0px";
    outer.style.visibility = "hidden";
    outer.style.width = "200px";
    outer.style.height = "150px";
    outer.style.overflow = "hidden";
    outer.appendChild(inner);

    document.body.appendChild(outer);
    outer.style.overflow = 'scroll';
    if (w1 == w2) w2 = outer.clientWidth;

    document.body.removeChild(outer);

    return (w1 - w2);
  }

  /**
   * @desc Get height of an element, including the margin
   * @param {HTMLElement} element
   * @return {Number} height
   */
  _getHeightWithMargin(element) {
    let styles = window.getComputedStyle(element);
    let height = parseInt(styles.getPropertyValue('height'));
    let margin = parseInt(styles.getPropertyValue('margin-top')) +
      parseInt(styles.getPropertyValue('margin-bottom'));

    return height + margin;
  }

  /**
   * Function that triggers the callbackKey when click event is triggered
   * @param {Event} event click event
   */
  _callbackFunction(event) {
    let eventKey = event.target.attributes['data-callback-key'];
    if(eventKey) {
        this.callbackKeyTriggered = eventKey.value;
    } else {
        this.callbackKeyTriggered = '';
    }
  }

  /**
   * Check if should hide the title and body separator
   * @return {Boolean}
   */
  _shouldHideTitleBodySeparator() {
    this.hideTitleBodySeparator = this.hideTitleBodySeparator || !this._hasTitle;
  }

  /**
   * Check if should hide the buttons separator
   * @return {Boolean}
   */
  _shouldHideButtonsSeparator() {
    return this.hideButtonsSeparator || !this._hasButtons;
  }

  /**
   * For a11y proposes, sometimes we need autofocus to the close button
   */
  _autoFocusCloseButton() {
    setTimeout(() => {
      this.$.wrapper.querySelector('#closeX').focus();
    }, 500);
  }
}

window.customElements.define(DclModal.is, DclModal);
